// Date, number and money formatting, bound to one locale.
//
// Nothing here is called directly by components: useI18n() builds a set of
// formatters for the current locale and hands them out, so switching language
// re-renders every date and amount on screen.

import { formatMoney as formatMoneyShared } from '@quoter/shared/money'
import { INTL_LOCALE, fmt, type Locale } from '@quoter/shared/locale'
import type { CoreStrings } from './index'

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const date = typeof value === 'string' ? new Date(value) : value
  return Number.isNaN(date.getTime()) ? null : date
}

export function createFormatters(locale: Locale, t: Pick<CoreStrings, 'time'>) {
  const tag = INTL_LOCALE[locale]

  /** "13 Sept 2026" in English; pt-PT's CLDR renders this skeleton as "13/09/2026". */
  function formatDate(value: string | Date | null | undefined): string {
    const date = toDate(value)
    return date ? date.toLocaleDateString(tag, { day: '2-digit', month: 'short', year: 'numeric' }) : ''
  }

  /** "Sunday, 13 September" — the overview's date line. */
  function formatLongDate(value: Date = new Date()): string {
    return value.toLocaleDateString(tag, { weekday: 'long', day: 'numeric', month: 'long' })
  }

  /** "13 Sept, 14:05" */
  function formatDateTime(value: string | Date | null | undefined): string {
    const date = toDate(value)
    return date
      ? date.toLocaleString(tag, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
      : ''
  }

  /** "14:05" — for messages received today. */
  function formatTime(value: string | Date | null | undefined): string {
    const date = toDate(value)
    return date ? date.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' }) : ''
  }

  /** "13 Sept" — for messages older than today. */
  function formatShortDate(value: string | Date | null | undefined): string {
    const date = toDate(value)
    return date ? date.toLocaleDateString(tag, { day: 'numeric', month: 'short' }) : ''
  }

  function relativeTime(iso: string): string {
    const diffSeconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (diffSeconds < 60) return t.time.justNow
    if (diffSeconds < 3600) return fmt(t.time.minutesAgo, { n: Math.floor(diffSeconds / 60) })
    if (diffSeconds < 86_400) return fmt(t.time.hoursAgo, { n: Math.floor(diffSeconds / 3600) })
    if (diffSeconds < 604_800) return fmt(t.time.daysAgo, { n: Math.floor(diffSeconds / 86_400) })
    return formatDate(iso)
  }

  function formatBytes(bytes: number): string {
    const [unit, value, digits] =
      bytes < 1024
        ? (['byte', bytes, 0] as const)
        : bytes < 1024 * 1024
          ? (['kilobyte', bytes / 1024, 0] as const)
          : (['megabyte', bytes / (1024 * 1024), 1] as const)
    return new Intl.NumberFormat(tag, {
      style: 'unit',
      unit,
      unitDisplay: 'short',
      maximumFractionDigits: digits,
    }).format(value)
  }

  function formatMoney(cents: number, currency = 'EUR'): string {
    return formatMoneyShared(cents, currency, tag)
  }

  return {
    formatDate,
    formatLongDate,
    formatDateTime,
    formatTime,
    formatShortDate,
    relativeTime,
    formatBytes,
    formatMoney,
  }
}

export type Formatters = ReturnType<typeof createFormatters>

/** Today in the local timezone as YYYY-MM-DD — the format <input type="date"> wants. */
export function todayIso(): string {
  const now = new Date()
  const offsetMs = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10)
}
